import { useEffect, useState, useRef } from 'react';
import { Users, Calendar, ClipboardCheck, BarChart3, Shield, Bell, Home, Vote, BookOpen, Music, HandHelping, Link2, Database, Lock, Eye, UserCheck, MessageSquare, Smartphone, TrendingUp, Repeat, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { Footer } from '../components/Footer';

const FEATURES_CAROUSEL = [
  { icon: Users, title: 'Gestão de Membros', desc: 'Cadastro completo com filas, funções e dados pessoais', color: '#6366F1', gradient: 'from-indigo-500 to-indigo-600' },
  { icon: Calendar, title: 'Eventos e Reuniões', desc: 'Agendamento com tipos configuráveis e recorrência', color: '#8B5CF6', gradient: 'from-purple-500 to-purple-600' },
  { icon: ClipboardCheck, title: 'Controle de Presenças', desc: 'Dashboard em tempo real com substituição automática', color: '#10B981', gradient: 'from-emerald-500 to-emerald-600' },
  { icon: Repeat, title: 'Escalas de Serviço', desc: 'Funções configuráveis com rotação de liderança', color: '#F59E0B', gradient: 'from-amber-500 to-amber-600' },
  { icon: Globe, title: 'Agenda Pública', desc: 'Timeline de eventos com inscrição no Google Calendar', color: '#3B82F6', gradient: 'from-blue-500 to-blue-600' },
  { icon: MessageSquare, title: 'WhatsApp', desc: 'Campanhas com progresso em tempo real e grupos', color: '#25D366', gradient: 'from-green-500 to-green-600' },
  { icon: Link2, title: 'Links Públicos', desc: 'Página Linktree personalizável com links de sistema', color: '#06B6D4', gradient: 'from-cyan-500 to-cyan-600' },
  { icon: BarChart3, title: 'Relatórios e Analytics', desc: 'Engajamento, acessos, conversões e fontes de tráfego', color: '#EC4899', gradient: 'from-pink-500 to-pink-600' },
  { icon: HandHelping, title: 'Solicitação de Visitas', desc: 'Formulário público com agendamento integrado', color: '#F97316', gradient: 'from-orange-500 to-orange-600' },
  { icon: Vote, title: 'Votações', desc: 'Decisões internas com controle de participação', color: '#EF4444', gradient: 'from-red-500 to-red-600' },
  { icon: Shield, title: 'Auditoria', desc: 'Registro imutável de todas as operações', color: '#64748B', gradient: 'from-slate-500 to-slate-600' },
  { icon: Calendar, title: 'Google Calendar', desc: 'Sync automático de aniversários e eventos públicos', color: '#4285F4', gradient: 'from-blue-400 to-blue-600' },
];

function FeatureCarousel() {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => { checkScroll(); }, []);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  return (
    <div className="relative">
      {canScrollLeft && (
        <button onClick={() => scroll(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors -ml-3">
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>
      )}
      {canScrollRight && (
        <button onClick={() => scroll(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors -mr-3">
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      )}
      <div ref={scrollRef} onScroll={checkScroll} className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 px-1 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {FEATURES_CAROUSEL.map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="flex-shrink-0 w-[280px] snap-start">
              <div className={`bg-gradient-to-br ${f.gradient} rounded-2xl p-6 h-44 flex flex-col justify-between shadow-lg`}>
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base">{f.title}</h3>
                  <p className="text-white/80 text-xs mt-1">{f.desc}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-center gap-1.5 mt-3">
        {FEATURES_CAROUSEL.map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full bg-slate-300" />
        ))}
      </div>
    </div>
  );
}

export default function SobrePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 flex items-center gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2662eb' }}>
            <Users className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">CCBRJM</h1>
            <p className="text-sm text-slate-500">Sistema de Gestão de Pessoas para Comunidades Religiosas</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 pt-8 sm:pt-12 pb-2 space-y-10">

        {/* 1. Identificação */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2662eb' }}>
              <Eye className="w-4 h-4 text-white" />
            </div>
            O que é o CCBRJM?
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-700 leading-relaxed space-y-3">
            <p>O <strong>CCBRJM</strong> é um sistema web de gestão administrativa desenvolvido exclusivamente para comunidades religiosas. A plataforma auxilia líderes e administradores no gerenciamento de membros, organização de eventos, controle de presenças, comunicação e muito mais.</p>
            <p>Trata-se de um <strong>projeto sem fins lucrativos</strong>, criado para servir e apoiar comunidades que trabalham com jovens e crianças. O sistema é operado sob o domínio <strong>ccbrjm.com</strong>, de propriedade do responsável pelo projeto.</p>
            <p>O CCBRJM opera no modelo <strong>multi-tenant</strong>, permitindo que múltiplas comunidades utilizem a plataforma de forma independente, com total isolamento de dados entre elas.</p>
          </div>
        </section>

        {/* 2. Funcionalidades */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2662eb' }}>
              <ClipboardCheck className="w-4 h-4 text-white" />
            </div>
            Funcionalidades do Sistema
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Users, title: 'Gestão de Membros', desc: 'Cadastro completo com dados pessoais, contatos, informações de batismo e participação musical. Organização por filas, funções de serviço e escalas.' },
              { icon: Calendar, title: 'Eventos e Reuniões', desc: 'Criação e agendamento com tipos configuráveis, recorrência automática e associação de pessoas. Gestão de reuniões com escalas e designações.' },
              { icon: ClipboardCheck, title: 'Controle de Presenças', desc: 'Registro de presenças com filtros por atribuição. Dashboard do auxiliar com status em tempo real, substituição automática e contagem de recitativos.' },
              { icon: Repeat, title: 'Escalas de Serviço', desc: 'Funções configuráveis com rotação automática de liderança, sugestão inteligente de escalas e gestão visual por evento.' },
              { icon: HandHelping, title: 'Solicitações de Visita', desc: 'Formulário público para solicitação de visitas com busca inteligente por telefone, agrupamento familiar e agendamento integrado.' },
              { icon: Vote, title: 'Votações', desc: 'Sistema de votações internas para decisões da comunidade, com controle de participação e resultados.' },
              { icon: BookOpen, title: 'Perguntas Públicas', desc: 'Canal público para envio de perguntas pela comunidade, com gestão centralizada pelo administrador.' },
              { icon: Globe, title: 'Agenda Pública', desc: 'Página pública com timeline de eventos, integração com Google Calendar e inscrição de membros com validação por WhatsApp.' },
              { icon: Link2, title: 'Links Públicos', desc: 'Página de links personalizável (estilo Linktree) com links de sistema integrados: cadastro, visitas, perguntas e agenda.' },
              { icon: MessageSquare, title: 'WhatsApp', desc: 'Envio de campanhas via WhatsApp com progresso em tempo real. Importação de contatos de grupos, associação a cadastros e convites automáticos.' },
              { icon: Bell, title: 'Comunicação por SMS', desc: 'Envio de mensagens SMS para membros, integrado à plataforma ClickSend, para comunicação direta.' },
              { icon: BarChart3, title: 'Relatórios e Analytics', desc: 'Dashboard com indicadores de presença, engajamento, aniversariantes. Analytics de links com acessos, cliques, conversões e fontes de tráfego.' },
              { icon: Calendar, title: 'Google Calendar', desc: 'Sincronização automática de aniversários e eventos públicos com o Google Calendar. Agenda pública para inscrição de membros.' },
              { icon: Shield, title: 'Auditoria Completa', desc: 'Registro imutável de todas as operações do sistema com filtros por módulo, ação e usuário. Visualização detalhada de alterações.' },
              { icon: Database, title: 'Backup', desc: 'Sistema de backup de dados acessível ao gestor para garantir a segurança e recuperação das informações.' },
              { icon: Smartphone, title: 'Complementos', desc: 'Módulos ativáveis: WhatsApp, Google Calendar, Escalas de Serviço, Perguntas e Votações. Cada igreja ativa apenas o que precisa.' },
              { icon: TrendingUp, title: 'Wizard de Ajuda', desc: 'Guia interativo passo-a-passo para auxiliares aprenderem a usar o sistema, acessível pelo menu do Dashboard Auxiliar.' },
              { icon: Users, title: 'Multi-Dashboard', desc: 'Três dashboards: Gestor (multi-igreja), Admin (gestão completa) e Auxiliar (presença, escalas, pessoas). Cada um com permissões específicas.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#eef2ff' }}>
                      <Icon className="w-4 h-4" style={{ color: '#2662eb' }} />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900">{item.title}</h3>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* 3. Dados solicitados e finalidade */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2662eb' }}>
              <Lock className="w-4 h-4 text-white" />
            </div>
            Dados Solicitados e Finalidade
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-700 leading-relaxed space-y-4">
            <p>O CCBRJM solicita acesso a dados do usuário exclusivamente para as finalidades descritas abaixo, de forma transparente e em conformidade com a Lei Geral de Proteção de Dados (LGPD):</p>

            <div className="rounded-xl bg-slate-50 p-4 space-y-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><UserCheck className="w-4 h-4" style={{ color: '#2662eb' }} /> Autenticação via Google (Login)</h3>
              <p><strong>Dados acessados:</strong> Nome, e-mail e foto do perfil da conta Google.</p>
              <p><strong>Finalidade:</strong> Identificar e autenticar o usuário no sistema de forma segura, sem necessidade de criar senha. O login é utilizado exclusivamente por administradores, auxiliares e gestores para acesso ao painel administrativo.</p>
              <p><strong>Escopo solicitado:</strong> <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">openid</code>, <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">profile</code>, <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">email</code></p>
            </div>

            <div className="rounded-xl bg-slate-50 p-4 space-y-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Calendar className="w-4 h-4" style={{ color: '#2662eb' }} /> Google Calendar (Opcional)</h3>
              <p><strong>Dados acessados:</strong> Permissão para criar e gerenciar eventos em agendas secundárias do Google Calendar.</p>
              <p><strong>Finalidade:</strong> Quando ativado pelo administrador, o sistema cria agendas exclusivas no Google Calendar: uma para aniversários dos membros (com eventos recorrentes anuais) e outra para a Agenda Pública de eventos da comunidade. A sincronização é automática ao cadastrar, editar ou excluir membros e eventos.</p>
              <p><strong>Escopo solicitado:</strong> <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">https://www.googleapis.com/auth/calendar</code></p>
              <p><strong>Controle do usuário:</strong> Esta integração é totalmente opcional. O administrador pode conectar e desconectar a qualquer momento em Configurações &gt; Complementos. Ao desconectar, as agendas são automaticamente excluídas da conta Google.</p>
            </div>

            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
              <p className="text-sm text-blue-800"><strong>Compromisso:</strong> O CCBRJM não acessa, armazena ou compartilha dados do Google além do estritamente necessário para as funcionalidades descritas acima. Não utilizamos dados do Google para publicidade, marketing, mineração de dados ou qualquer outro fim não relacionado à operação do sistema.</p>
            </div>
          </div>
        </section>

        {/* 4. Segurança e Privacidade */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2662eb' }}>
              <Shield className="w-4 h-4 text-white" />
            </div>
            Segurança e Privacidade
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-700 leading-relaxed space-y-3">
            <p>A proteção dos dados dos nossos usuários é uma prioridade. O CCBRJM adota as seguintes medidas:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Criptografia:</strong> Toda comunicação é protegida por HTTPS/TLS.</li>
              <li><strong>Autenticação segura:</strong> Login via Google OAuth 2.0 com tokens de sessão.</li>
              <li><strong>Isolamento de dados:</strong> Arquitetura multi-tenant com segregação completa entre comunidades.</li>
              <li><strong>Controle de acesso:</strong> Três níveis de permissão (Gestor, Administrador, Auxiliar) com princípio do menor privilégio.</li>
              <li><strong>Auditoria:</strong> Registro completo de todas as operações para rastreabilidade.</li>
              <li><strong>Conformidade LGPD:</strong> Tratamento de dados em conformidade com a Lei Geral de Proteção de Dados (Lei n. 13.709/2018).</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <Shield className="w-4 h-4" style={{ color: '#2662eb' }} /> Política de Privacidade
              </a>
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <ClipboardCheck className="w-4 h-4" style={{ color: '#2662eb' }} /> Termos de Uso
              </a>
            </div>
          </div>
        </section>

        {/* 5. Contato */}
        <section>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-700 leading-relaxed">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Contato</h2>
            <p>Para dúvidas, sugestões ou solicitações relacionadas ao sistema, entre em contato:</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 rounded-lg w-fit">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2662eb' }}>
                  <Home className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Website</p>
                  <p className="text-sm font-medium text-slate-900">ccbrjm.com</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}
